import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { SpinnerGap, MagnifyingGlass, VideoCamera } from "@phosphor-icons/react";
import { TONES } from "@/lib/generateConstants";
import { GOALS, AUDIENCES, TONE_MODES, DURATIONS, RESEARCH_MODES, DOCUMENT_PURPOSES } from "./talkingDocConstants";

export function TalkingDocForm({ goal, setGoal, audience, setAudience, title, setTitle, minutes, setMinutes,
  toneMode, setToneMode, toneVariations, toggleToneVariation,
  offerCta, setOfferCta, videoObjective, setVideoObjective,
  audienceContext, setAudienceContext, mustInclude, setMustInclude, avoidSaying, setAvoidSaying,
  aiModel, setAiModel, availableModels,
  includeTimestampsScript, setIncludeTimestampsScript,
  includeTimestampsOutline, setIncludeTimestampsOutline,
  includeVisualCuesScript, setIncludeVisualCuesScript,
  researchNotes, setResearchNotes, researching, handleResearch,
  researchMode, setResearchMode,
  documentPurpose, setDocumentPurpose,
  referenceVideo, setReferenceVideo,
  generating, loadingMsg, handleGenerate }) {

  return (
    <div className="space-y-6">
      {/* Row 1: Goal + Audience + Tone Mode */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Video Goal *</label>
          <Select value={goal} onValueChange={setGoal}>
            <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="td-goal"><SelectValue placeholder="Select goal" /></SelectTrigger>
            <SelectContent>{GOALS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Audience *</label>
          <Select value={audience} onValueChange={setAudience}>
            <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="td-audience"><SelectValue placeholder="Select audience" /></SelectTrigger>
            <SelectContent>{AUDIENCES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Tone Mode *</label>
          <Select value={toneMode} onValueChange={setToneMode}>
            <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="td-tone"><SelectValue placeholder="Select tone" /></SelectTrigger>
            <SelectContent>{TONE_MODES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Tone Variations Chips */}
      <div>
        <label className="block text-xs font-medium text-[var(--text-muted)] mb-2 uppercase tracking-wider">
          Tone Variations <span className="normal-case font-normal">(optional — fine-tune your voice)</span>
        </label>
        <div className="flex flex-wrap gap-1.5" data-testid="td-tone-chips">
          {TONES.map(tone => (
            <button key={tone} onClick={() => toggleToneVariation(tone)} data-testid={`td-tone-chip-${tone.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                toneVariations.includes(tone)
                  ? "bg-[#FF4400]/15 border-[#FF4400]/50 text-[#FF4400]"
                  : "bg-[var(--bg-card)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]"
              }`}>
              {tone}
            </button>
          ))}
        </div>
        {toneVariations.length > 0 && (
          <p className="text-[10px] text-[var(--text-muted)] mt-1.5">
            Selected: {toneVariations.join(", ")}
          </p>
        )}
      </div>

      {/* Row 2: Title */}
      <div>
        <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Topic / Title *</label>
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., 3 Mistakes Coaches Make on LinkedIn" className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="td-title" />
      </div>

      {/* Research Assist */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Research Assist</label>
          <Select value={researchMode} onValueChange={setResearchMode}>
            <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="td-research-mode"><SelectValue placeholder="Select research depth" /></SelectTrigger>
            <SelectContent>{RESEARCH_MODES.map(mode => <SelectItem key={mode.value} value={mode.value}>{mode.label} — {mode.help}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Button onClick={handleResearch} disabled={researching || !aiModel || title.trim().length < 6}
            variant="outline" data-testid="td-research-btn"
            className="w-full h-10 text-sm border-dashed border-[var(--border-strong)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:border-[#FF4400]/40 hover:text-[#FF4400] transition-all gap-2">
            {researching
              ? <><SpinnerGap size={16} className="animate-spin" />Researching topic...</>
              : <><MagnifyingGlass size={16} />Run Research</>}
          </Button>
        </div>
      </div>

      {/* Research Notes (shown if populated or user wants to type) */}
      {(researchNotes || researching) && (
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">
            Research Notes <span className="normal-case font-normal">(auto-filled or type your own)</span>
          </label>
          <Textarea value={researchNotes} onChange={e => setResearchNotes(e.target.value)}
            placeholder="Research findings will appear here..."
            rows={5} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-y" data-testid="td-research-notes" />
          {researchNotes && (
            <button onClick={() => setResearchNotes("")}
              className="text-[10px] text-[var(--text-muted)] hover:text-red-400 mt-1 transition-colors">
              Clear research
            </button>
          )}
        </div>
      )}

      {/* Row 3: Duration + Objective */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Desired Length *</label>
          <div className="flex gap-1.5 flex-wrap">
            {DURATIONS.map(d => (
              <button key={d} onClick={() => setMinutes(d)} data-testid={`td-duration-${d}`}
                className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${minutes === d
                  ? "bg-[#FF4400]/10 border-[#FF4400] text-[#FF4400]"
                  : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]"
                }`}>
                {d} min
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Video Objective</label>
          <Input value={videoObjective} onChange={e => setVideoObjective(e.target.value)} placeholder="e.g., Position myself as the go-to authority for..." className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="td-objective" />
        </div>
      </div>

      {/* Row 4: Audience Context + Offer/CTA */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Audience Context</label>
          <Textarea value={audienceContext} onChange={e => setAudienceContext(e.target.value)} placeholder="What does your audience currently believe or know about this topic?" rows={3} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-none" data-testid="td-audience-context" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Offer / CTA (optional)</label>
          <Textarea value={offerCta} onChange={e => setOfferCta(e.target.value)} placeholder="e.g., Book a free strategy call at mysite.com/call" rows={3} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-none" data-testid="td-offer-cta" />
        </div>
      </div>

      {/* Row 5: Must Include + Avoid Saying */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Must Include <span className="text-[var(--text-muted)]">(one per line)</span></label>
          <Textarea value={mustInclude} onChange={e => setMustInclude(e.target.value)} placeholder={"Framework name\nSpecific stat or reference\nClient success story"} rows={4} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-none font-mono" data-testid="td-must-include" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Avoid Saying <span className="text-[var(--text-muted)]">(one per line)</span></label>
          <Textarea value={avoidSaying} onChange={e => setAvoidSaying(e.target.value)} placeholder={"game-changer\nlevel up\n10x"} rows={4} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-none font-mono" data-testid="td-avoid-saying" />
        </div>
      </div>

      {/* Generation metadata toggles + Model + Generate */}
      <div className="flex flex-col gap-4">
        <div className="text-[11px] text-[var(--text-muted)] bg-[var(--bg-card)] border border-[var(--border)] rounded-lg px-3 py-2">
          <strong className="text-[var(--text-secondary)]">Output guide:</strong> Script = spoken delivery asset. Document Outline = production handoff asset for slides, Notion, Google Docs, or lead magnets.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="td-generation-toggles">
          <div className="flex items-center gap-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-3">
            <Switch checked={includeTimestampsScript} onCheckedChange={setIncludeTimestampsScript} id="script-timestamps-toggle" data-testid="td-script-timestamps-toggle"
              className="data-[state=checked]:bg-[#FF4400]" />
            <label htmlFor="script-timestamps-toggle" className="text-sm text-[var(--text-secondary)] cursor-pointer select-none">
              Script timestamps
            </label>
          </div>

          <div className="flex items-center gap-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-3">
            <Switch checked={includeTimestampsOutline} onCheckedChange={setIncludeTimestampsOutline} id="outline-timestamps-toggle" data-testid="td-outline-timestamps-toggle"
              className="data-[state=checked]:bg-[#FF4400]" />
            <label htmlFor="outline-timestamps-toggle" className="text-sm text-[var(--text-secondary)] cursor-pointer select-none">
              Outline timestamps
            </label>
          </div>

          <div className="flex items-center gap-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-3">
            <Switch checked={includeVisualCuesScript} onCheckedChange={setIncludeVisualCuesScript} id="script-visual-cues-toggle" data-testid="td-script-visual-cues-toggle"
              className="data-[state=checked]:bg-[#FF4400]" />
            <label htmlFor="script-visual-cues-toggle" className="text-sm text-[var(--text-secondary)] cursor-pointer select-none">
              Script visual cues
            </label>
          </div>
        </div>

        {/* Document Purpose + Reference Video */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">Document Purpose</label>
            <Select value={documentPurpose} onValueChange={setDocumentPurpose}>
              <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="td-document-purpose"><SelectValue placeholder="Select purpose" /></SelectTrigger>
              <SelectContent>{DOCUMENT_PURPOSES.map(p => <SelectItem key={p.value} value={p.value}>{p.label} — {p.help}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-3 self-end">
            <Switch checked={referenceVideo} onCheckedChange={setReferenceVideo} id="reference-video-toggle" data-testid="td-reference-video-toggle"
              className="data-[state=checked]:bg-[#FF4400]" />
            <label htmlFor="reference-video-toggle" className="text-sm text-[var(--text-secondary)] cursor-pointer select-none">
              Reference companion video in document
            </label>
          </div>
        </div>

        {/* Model + Generate */}
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1 w-full">
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5 uppercase tracking-wider">AI Model</label>
            <Select value={aiModel} onValueChange={setAiModel}>
              <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="td-model"><SelectValue placeholder="Select model" /></SelectTrigger>
              <SelectContent>{availableModels.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={handleGenerate} disabled={generating || !availableModels.length}
            className="h-11 px-8 bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold rounded-xl transition-all w-full sm:w-auto" data-testid="td-generate-btn">
            {generating ? <><SpinnerGap size={16} className="animate-spin mr-2" />{loadingMsg}</> : <><VideoCamera size={18} className="mr-2" />Generate Talking Doc</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
